import { useQuery } from '@tanstack/react-query'
import { useAuth } from 'context/AuthProvider'
import { isClaimsAdmin } from 'supabase/auth'

/**
 * Exposes the signed-in user's administrator claim, evaluated server-side.
 *
 * Fails closed by construction: the hook reports `isAdmin: false` while the
 * claim is in flight, when the query rejects, and when nobody is signed in.
 * `isLoading` is what lets a consumer tell "not yet" apart from "no", so a
 * guard can refuse to render rather than guess.
 */
export const useIsAdmin = () => {
  const { user } = useAuth()

  const { data, isLoading, isError } = useQuery({
    // The user id belongs in the key for security, not tidiness: without it,
    // signing out and back in as a different account in the same tab would be
    // served the previous account's cached verdict.
    queryKey: ['isAdmin', user?.id ?? null],
    queryFn: isClaimsAdmin,
    // Never ask the database about the claim of nobody.
    enabled: !!user,
    // A denial must be prompt. Retrying would hold the guard in its loading
    // state, and a guard that renders nothing while loading reads as a broken
    // page.
    retry: false,
    // Overrides the global one-hour staleTime set for every query in
    // utils/queryClient.ts, so a revoked administrator loses UI access within
    // five minutes rather than a full session.
    staleTime: 1000 * 60 * 5,
  })

  // Collapse once, here. Returning the raw query data would hand the consumer
  // `boolean | undefined`, and `undefined` in a boolean position is exactly
  // how a guard fails open.
  return { isAdmin: data === true, isLoading, isError }
}
